// packages/shared/utils/validation.js
// Pure validation logic shared between frontend and backend

/**
 * Validate email format
 * @param {string} email
 * @returns {boolean}
 */
function validateEmail(email) {
  if (!email || typeof email !== 'string') return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim().toLowerCase());
}

/**
 * Validate phone number format
 * @param {string} phone
 * @returns {boolean}
 */
function validatePhone(phone) {
  if (!phone || typeof phone !== 'string') return false;
  const phoneRegex = /^\+?[\d\s\-\(\)]+$/;
  const cleaned = phone.replace(/\D/g, '');
  return phoneRegex.test(phone) && cleaned.length >= 10;
}

/**
 * Validate password strength
 * @param {string} password
 * @returns {{ valid: boolean, errors: string[] }}
 */
function validatePassword(password) {
  const errors = [];

  if (!password || password.length < 8) {
    errors.push("Password must be at least 8 characters");
  }

  if (!/[A-Z]/.test(password)) {
    errors.push("Must contain an uppercase letter");
  }

  if (!/[a-z]/.test(password)) {
    errors.push("Must contain a lowercase letter");
  }

  if (!/[0-9]/.test(password)) {
    errors.push("Must contain a number");
  }

  if (!/[!@#$%^&*()\-,.?":{}|<>]/.test(password)) {
    errors.push("Must contain a special character");
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Validate required string field
 * @param {string} value
 * @param {string} fieldName
 * @param {object} options
 * @returns {{ valid: boolean, error: string|null }}
 */
function validateRequired(value, fieldName, options = {}) {
  const { minLength = 1, maxLength = null } = options;

  if (!value || typeof value !== 'string') {
    return { valid: false, error: `${fieldName} is required` };
  }

  const trimmed = value.trim();

  if (trimmed.length < minLength) {
    return { valid: false, error: `${fieldName} must be at least ${minLength} characters` };
  }

  if (maxLength && trimmed.length > maxLength) {
    return { valid: false, error: `${fieldName} must not exceed ${maxLength} characters` };
  }

  return { valid: true, error: null };
}

/**
 * Validate UUID format
 * @param {string} uuid
 * @returns {boolean}
 */
function validateUuid(uuid) {
  if (!uuid || typeof uuid !== 'string') return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
}

/**
 * Validate date is not in the past
 * @param {Date|string} date
 * @returns {boolean}
 */
function validateFutureDate(date) {
  const parsedDate = new Date(date);
  if (isNaN(parsedDate.getTime())) return false;
  return parsedDate > new Date();
}

/**
 * Validate date is a valid date
 * @param {Date|string} date
 * @returns {boolean}
 */
function validateDate(date) {
  const parsedDate = new Date(date);
  return !isNaN(parsedDate.getTime());
}

module.exports = {
  validateEmail,
  validatePhone,
  validatePassword,
  validateRequired,
  validateUuid,
  validateFutureDate,
  validateDate
};
